import { SdkContext } from '../internal'
import { isRealtimeConnected } from '@/lib/realtime/status'

/** F-105: the closed list of field kinds the API accepts (`@openapi` of `POST /api/ai/improve-text`). */
export type AiFieldKind =
  | 'post' | 'event' | 'product' | 'course' | 'lesson' | 'call' | 'proposal'
  | 'campaign' | 'feedback' | 'recognition' | 'classified' | 'download' | 'survey' | 'other'

export type AiTextFormat = 'html' | 'markdown' | 'plain'

export interface ImprovedText {
  text: string
  format: AiTextFormat
  /** False when the model had nothing to improve (the text comes back as it went). */
  changed: boolean
}

export interface AiUsageRow {
  uses: number
  inputTokens: number
  outputTokens: number
  costUsd: number
}

export interface AiUsageReport {
  period: { from: string; to: string }
  totals: AiUsageRow & { failures: number }
  byCommunity: (AiUsageRow & { communityId: string; communityName: string | null })[]
  byFieldKind: (AiUsageRow & { fieldKind: string })[]
  byDay: (AiUsageRow & { day: string })[]
  /** Per provider (SuperAdmin only, names allowed there): text models and speech-to-text. */
  byProvider?: AiProviderRow[]
  byProviderDay?: (Omit<AiProviderRow, 'failures' | 'cacheHits' | 'audioMinutes'> & { day: string })[]
}

export interface AiProviderRow {
  provider: 'anthropic' | 'elevenlabs' | string
  uses: number
  failures: number
  cacheHits: number
  inputTokens: number
  outputTokens: number
  audioSeconds: number
  audioMinutes: number
  costUsd: number
}

/** F-107: why a Drop's text could not be written (translated in the app). */
export type DropDescriptionError = 'tooLong' | 'noSpeech' | 'unavailable'

/** F-107: a transcription job (`POST /api/ai/drop-description`, 202), as polled or pushed on the socket. */
export interface DropDescriptionJob {
  jobId: string
  status: 'pending' | 'transcribed' | 'done' | 'failed'
  mediaId: string
  kind: 'content' | 'status' | 'comment'
  format: 'html' | 'plain'
  description: string | null
  language: string
  durationSeconds: number | null
  error: DropDescriptionError | null
  errorMessage?: string
  transcript?: string
  createdAt: string
  completedAt: string | null
}

export interface DropDescription {
  description: string
  transcript?: string
}

/** A finished job that wrote nothing: `code` is the API's reason, `message` its translated text. */
export class DropDescriptionFailed extends Error {
  constructor(public code: DropDescriptionError | null, message?: string) {
    super(message || code || 'drop-description-failed')
  }
}

/** Window event the platform socket relays `ai.dropDescription.ready` on (RealtimeProvider). */
export const DROP_DESCRIPTION_EVENT = 'memberfy:drop-description'

const DROP_DESCRIPTION_POLL_MS = 3000
const DROP_DESCRIPTION_TIMEOUT_MS = 180_000

const isSettled = (job: DropDescriptionJob | null | undefined) => job?.status === 'done' || job?.status === 'failed'

/** Longest text the API takes in one request (400 above it). */
export const AI_MAX_TEXT_LENGTH = 20000
export const AI_MAX_INSTRUCTION_LENGTH = 500

/** F-105: "Improve with AI". `improveText` is open to every role of the community; `getAiUsage` is SuperAdmin only. */
export class AiResource {
  constructor(private ctx: SdkContext) {}

  /**
   * 400 (text too long, bad field kind), 429 (the anti-abuse brake, SEC-187) and 503 (provider unavailable)
   * carry a translated message to show as is; a 429 also carries `retryAfter` (seconds) on the error.
   */
  async improveText(body: { text: string; fieldKind: AiFieldKind; format: AiTextFormat; instruction?: string }): Promise<ImprovedText> {
    return this.ctx.callKeepingRetryAfter({ httpMethod: 'POST', path: '/ai/improve-text', hasBody: true }, { body })
  }

  /**
   * F-107: writes a Drop's text from its audio (`kind: 'status'` is a first-person caption of up to 280
   * characters). The job is created with 202; outside production it already comes back done. Otherwise the
   * result comes from the socket's `ai.dropDescription.ready`; the job is polled only while the socket is
   * down (F-110), and read once more before giving up.
   * Rejects with `DropDescriptionFailed` when the job fails, so the person writes the text instead.
   */
  async generateDropDescription(
    body: { mediaId: string; kind?: 'content' | 'status' | 'comment' | 'lesson'; title?: string; language?: string; format?: 'html' | 'plain' },
    options: { signal?: AbortSignal } = {},
  ): Promise<DropDescription> {
    const { signal } = options
    const created = await this.ctx.callKeepingRetryAfter<DropDescriptionJob>(
      { httpMethod: 'POST', path: '/ai/drop-description', hasBody: true }, { body: { format: 'plain', ...body }, signal },
    )
    const job = isSettled(created) ? created : await this.waitForDropDescription(created.jobId, signal)
    if (job.status === 'failed' || !job.description) throw new DropDescriptionFailed(job.error, job.errorMessage)
    return { description: job.description, ...(job.transcript ? { transcript: job.transcript } : {}) }
  }

  private waitForDropDescription(jobId: string, signal?: AbortSignal): Promise<DropDescriptionJob> {
    return new Promise((resolve, reject) => {
      let done = false
      let timer: ReturnType<typeof setTimeout> | undefined
      const startedAt = Date.now()
      const finish = (job: DropDescriptionJob | null, error?: unknown) => {
        if (done) return
        done = true
        clearTimeout(timer)
        window.removeEventListener(DROP_DESCRIPTION_EVENT, onSocket)
        signal?.removeEventListener('abort', onAbort)
        if (job) resolve(job)
        else reject(error)
      }
      const onSocket = (event: Event) => {
        const job = (event as CustomEvent<DropDescriptionJob>).detail
        if (job?.jobId === jobId && isSettled(job)) finish(job)
      }
      const onAbort = () => finish(null, new DOMException('Aborted', 'AbortError'))
      const poll = async () => {
        if (done) return
        const timedOut = Date.now() - startedAt > DROP_DESCRIPTION_TIMEOUT_MS
        /* Socket up: it brings the result, nothing is fetched until the last look at the timeout. */
        if (!timedOut && isRealtimeConnected()) {
          timer = setTimeout(() => void poll(), DROP_DESCRIPTION_POLL_MS)
          return
        }
        try {
          const job = await this.ctx.call<DropDescriptionJob>({ httpMethod: 'GET', path: '/ai/drop-description/{jobId}' }, { jobId, signal })
          if (isSettled(job)) { finish(job); return }
          if (timedOut) { finish(null, new DropDescriptionFailed('unavailable')); return }
        } catch (error) {
          if (signal?.aborted) return
          // A 4xx will not change on retry; a network blip may.
          const status = (error as { response?: { status?: number } })?.response?.status
          if (status && status < 500 && status !== 429) { finish(null, error); return }
          if (timedOut) { finish(null, new DropDescriptionFailed('unavailable')); return }
        }
        if (!done) timer = setTimeout(() => void poll(), DROP_DESCRIPTION_POLL_MS)
      }
      if (signal?.aborted) { onAbort(); return }
      window.addEventListener(DROP_DESCRIPTION_EVENT, onSocket)
      signal?.addEventListener('abort', onAbort, { once: true })
      timer = setTimeout(() => void poll(), DROP_DESCRIPTION_POLL_MS)
    })
  }

  /** Totals for a period (default: last 30 days). Internal data: never shown to a community. */
  async getAiUsage(params: { from?: string; to?: string; communityId?: string } = {}): Promise<AiUsageReport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/ai-usage' }, { query: params })
  }
}
